import {
  type CameraMedia,
  type CameraMediaKind,
  type CameraMediaTrigger,
  queryKeys,
} from '@mevaser/shared'
import { Badge, type BadgeProps, EmptyState } from '@mevaser/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Camera, Film, Image as ImageIcon, Monitor, Paperclip, User } from 'lucide-react'
import { useCallback } from 'react'
import { api } from '../lib/api.js'
import { InfiniteScrollFooter } from './data-table.js'

const PAGE_SIZE = 48

export interface CameraMediaFilters {
  machineId?: string
  cameraId?: string
  kind?: CameraMediaKind
  trigger?: CameraMediaTrigger
  from?: string
  to?: string
}

export function useCameraMedia(filters: CameraMediaFilters) {
  const params = { ...filters, pageSize: PAGE_SIZE }
  const query = useInfiniteQuery({
    queryKey: queryKeys.cameras.media(params),
    queryFn: ({ pageParam }) =>
      api.get<{ items: CameraMedia[]; total: number; page: number; pageSize: number }>(
        '/cameras/media',
        { query: { ...params, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    // A capture in flight is a row the kiosk is about to fill in.
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.items.some((i) => i.status === 'requested')) ? 3000 : false,
  })
  return {
    ...query,
    items: query.data?.pages.flatMap((p) => p.items) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
  }
}

export const MEDIA_KIND_LABEL: Record<CameraMediaKind, string> = {
  snapshot: 'Snapshot',
  clip: 'Video clip',
  photo: 'Customer photo',
  screenshot: 'Screen capture',
}

export const MEDIA_KIND_ICON: Record<CameraMediaKind, typeof Film> = {
  snapshot: ImageIcon,
  clip: Film,
  photo: User,
  screenshot: Monitor,
}

export const MEDIA_STATUS_LABEL: Record<CameraMedia['status'], string> = {
  requested: 'Waiting for the kiosk',
  ready: 'Ready',
  failed: 'Failed',
  timed_out: 'No response',
}

const STATUS_TONE: Record<CameraMedia['status'], NonNullable<BadgeProps['tone']>> = {
  requested: 'info',
  ready: 'success',
  failed: 'danger',
  timed_out: 'warning',
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** When the picture was taken, or when it was asked for if it never arrived. */
export function mediaTime(media: CameraMedia): string {
  return new Date(media.capturedAt ?? media.createdAt).toLocaleString()
}

/**
 * Snapshots and clips as a card grid with the tables' infinite-scroll footer.
 * Cards carry metadata only: the footage is viewed (and the view audited)
 * when someone opens it, never loaded just by scrolling past.
 */
export function CameraMediaGrid({
  filters,
  onSelect,
  showMachine = true,
}: {
  filters: CameraMediaFilters
  onSelect: (media: CameraMedia) => void
  showMachine?: boolean
}) {
  const { items, total, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCameraMedia(filters)
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage])

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-foreground-muted">Loading captures…</p>
  }
  if (isError) {
    return <p className="py-8 text-center text-sm text-danger">Could not load the captures.</p>
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={Camera}
        title="No captures yet"
        description="Use Capture to take a snapshot or short clip from a kiosk camera, or a photo or screen capture from a kiosk tablet. Tablet screens are also captured automatically on checkout errors when that is switched on in Settings. Everything is kept for 90 days unless attached to a support ticket."
      />
    )
  }

  return (
    <div>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3 p-3">
        {items.map((media) => {
          const Icon = MEDIA_KIND_ICON[media.kind]
          return (
            <li key={media.id}>
              <button
                type="button"
                disabled={media.status !== 'ready'}
                onClick={() => onSelect(media)}
                className="flex h-full w-full flex-col gap-2 rounded-lg border border-border bg-surface p-3 text-left enabled:cursor-pointer enabled:hover:border-border-strong enabled:hover:bg-surface-sunken disabled:opacity-80"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <Icon className="size-4 text-foreground-muted" aria-hidden="true" />
                    {MEDIA_KIND_LABEL[media.kind]}
                  </span>
                  <Badge tone={STATUS_TONE[media.status]}>{MEDIA_STATUS_LABEL[media.status]}</Badge>
                </div>
                <div className="min-w-0 space-y-0.5">
                  <p className="truncate text-sm">
                    {showMachine ? `${media.machineName} · ` : ''}
                    {media.cameraName}
                  </p>
                  <p className="text-xs text-foreground-muted tabular">{mediaTime(media)}</p>
                  {media.trigger === 'checkout_error' ? (
                    <p className="flex flex-wrap items-center gap-1.5 text-xs">
                      <Badge tone="warning">Auto · checkout error</Badge>
                      {media.orderNumber ? (
                        <span className="text-foreground-muted">Order {media.orderNumber}</span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
                {media.error && media.status !== 'ready' ? (
                  <p className="text-xs text-danger">{media.error}</p>
                ) : null}
                <div className="mt-auto flex items-center justify-between gap-2 text-xs text-foreground-muted">
                  <span className="tabular">
                    {media.sizeBytes !== null ? formatSize(media.sizeBytes) : ''}
                    {media.kind === 'clip' && media.durationSeconds
                      ? `${media.sizeBytes !== null ? ' · ' : ''}${media.durationSeconds}s`
                      : ''}
                  </span>
                  {media.ticketCount > 0 ? (
                    <Badge tone="primary">
                      <Paperclip className="size-3" aria-hidden="true" />
                      {media.ticketCount === 1 ? 'On a ticket' : `On ${media.ticketCount} tickets`}
                    </Badge>
                  ) : null}
                </div>
              </button>
            </li>
          )
        })}
      </ul>
      <InfiniteScrollFooter
        total={total}
        loaded={items.length}
        hasMore={!!hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={loadMore}
      />
    </div>
  )
}
